import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@descope/react-sdk';
import { setActiveTenantId } from '../services/api';

const STORAGE_KEY = 'brandlight.tenantId';
const NAME_STORAGE_KEY = 'brandlight.tenantName';

interface TenantContextValue {
  /** Currently active DB tenant id (sent as X-Tenant-Id). Null until the user picks one. */
  activeTenantId: number | null;
  /** Display name of the active tenant, persisted alongside the id. */
  activeTenantName: string | null;
  /** Switch to a different tenant. Invalidates the TanStack Query cache. */
  refreshTenantName: (tenantId: number, name: string) => void;
  switchTenant: (tenantId: number, tenantName?: string, retainedQueryKey?: readonly unknown[]) => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

function readPersistedId(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

function readPersistedName(): string | null {
  try {
    return localStorage.getItem(NAME_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isSessionLoading } = useSession();
  const queryClient = useQueryClient();

  // Always read from localStorage on init — Descope's isAuthenticated is false
  // on the first render (async hydration), so we can't gate on it here.
  // The logout effect below clears storage when the session ends.
  const [activeTenantId, setActiveTenantIdState] = useState<number | null>(readPersistedId);
  const [activeTenantName, setActiveTenantNameState] = useState<string | null>(readPersistedName);


  // Clear tenant state on logout. Guard with isSessionLoading so we don't
  // clear on the initial render when isAuthenticated is still false while
  // Descope hydrates the session from storage.
  useEffect(() => {
    if (!isSessionLoading && !isAuthenticated) {
      setActiveTenantId(null);
      void queryClient.cancelQueries();
      queryClient.removeQueries();
      setActiveTenantIdState(null);
      setActiveTenantNameState(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(NAME_STORAGE_KEY);
    }
  }, [isAuthenticated, isSessionLoading, queryClient]);

  const switchTenant = useCallback(
    (tenantId: number, tenantName?: string, retainedQueryKey?: readonly unknown[]) => {
      setActiveTenantId(tenantId);
      void queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] !== 'me' && (!retainedQueryKey || query.queryHash !== hashKey(retainedQueryKey)) });
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' && (!retainedQueryKey || query.queryHash !== hashKey(retainedQueryKey)) });
      localStorage.setItem(STORAGE_KEY, String(tenantId));
      if (tenantName !== undefined) {
        localStorage.setItem(NAME_STORAGE_KEY, tenantName);
      }
      setActiveTenantIdState(tenantId);
      setActiveTenantNameState(tenantName ?? null);
    },
    [queryClient],
  );

  const refreshTenantName = useCallback((tenantId: number, name: string) => {
    if (tenantId !== activeTenantId) return;
    setActiveTenantNameState(name);
    localStorage.setItem(NAME_STORAGE_KEY, name);
  }, [activeTenantId]);
  const value = useMemo<TenantContextValue>(
    () => ({ activeTenantId, activeTenantName, switchTenant, refreshTenantName }),
    [activeTenantId, activeTenantName, switchTenant, refreshTenantName],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}
